/**
 * The theme for the org a person is in (the 2026-10-06 next-phase plan, step 3): the active org's saved look, read from
 * the org (GET /v1/platform/branding) and kept in this browser per org so a reload paints it before the read answers;
 * saving it for an org admin of a live org (PUT). The look itself, the person's own mode and the product's name are
 * TenantThemeProvider's (lib/tenant-theme.tsx, lib/theme/org-look.ts).
 */
import { useCallback, useEffect, useMemo, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { brandingApi, ORG_BRANDING_KEY } from "@/lib/api/branding";
import { TenantThemeProvider } from "@/lib/tenant-theme";
import { orgLookCacheKey, orgLookOf, type OrgLook } from "./org-look";
import type { TenantTheme } from "./types";

const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

/** The look last read for an org in this browser; none when there is none or storage does not read. */
function readCachedLook(orgId: string): Partial<OrgLook> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(orgLookCacheKey(orgId));
    return raw ? orgLookOf(JSON.parse(raw)) : {};
  } catch {
    return {};
  }
}

function writeCachedLook(orgId: string, look: Partial<OrgLook>) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(orgLookCacheKey(orgId), JSON.stringify(look));
  } catch {
    /* quota or disabled storage: the next read paints it */
  }
}

export function OrgThemeProvider({ initial, children }: { initial?: Partial<TenantTheme>; children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { activeOrgId, canAdmin } = useOrg();
  const live = isLiveApiFlag && isAuthenticated && activeOrgId !== null;
  const qc = useQueryClient();
  const branding = useQuery({
    queryKey: [...ORG_BRANDING_KEY, activeOrgId ?? ""],
    queryFn: brandingApi.get,
    enabled: live,
    staleTime: 5 * 60_000,
  });
  const saved = useMemo<Partial<OrgLook>>(() => {
    if (branding.data) return orgLookOf(branding.data.theme);
    return activeOrgId ? readCachedLook(activeOrgId) : {};
  }, [branding.data, activeOrgId]);
  useEffect(() => {
    if (activeOrgId && branding.data) writeCachedLook(activeOrgId, orgLookOf(branding.data.theme));
  }, [activeOrgId, branding.data]);
  const put = useCallback(
    async (look: OrgLook) => {
      if (!activeOrgId) return;
      const stored = await brandingApi.put(look);
      qc.setQueryData([...ORG_BRANDING_KEY, activeOrgId], stored);
    },
    [activeOrgId, qc],
  );
  return (
    <TenantThemeProvider initial={initial} orgId={activeOrgId} saved={saved} saveLook={live && canAdmin ? put : undefined}>
      {children}
    </TenantThemeProvider>
  );
}
